import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  Shuffle,
  ArrowRight,
  Scissors,
  RotateCw,
} from "lucide-react";

const ReorderListVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Even length",
      list: [1, 2, 3, 4],
      expected: [1, 4, 2, 3],
    },
    {
      id: 2,
      name: "Odd length",
      list: [1, 2, 3, 4, 5],
      expected: [1, 5, 2, 4, 3],
    },
    {
      id: 3,
      name: "Two elements",
      list: [1, 2],
      expected: [1, 2],
    },
    {
      id: 4,
      name: "Single element",
      list: [1],
      expected: [1],
    },
    {
      id: 5,
      name: "Six elements",
      list: [1, 2, 3, 4, 5, 6],
      expected: [1, 6, 2, 5, 3, 4],
    },
    {
      id: 6,
      name: "Large list",
      list: [1, 2, 3, 4, 5, 6, 7, 8],
      expected: [1, 8, 2, 7, 3, 6, 4, 5],
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [list, setList] = useState(testCases[0].list);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Phase tracking
  const [currentPhase, setCurrentPhase] = useState("");

  // Pseudo code
  const pseudoCode = [
    { line: "function reorderList(head):", indent: 0 },
    { line: "// Step 1: Find the middle of the list", indent: 1 },
    { line: "slow = fast = head", indent: 1 },
    { line: "while fast.next && fast.next.next:", indent: 1 },
    { line: "slow = slow.next", indent: 2 },
    { line: "fast = fast.next.next", indent: 2 },
    { line: "", indent: 0 },
    { line: "// Step 2: Split list into two halves", indent: 1 },
    { line: "secondHalf = slow.next", indent: 1 },
    { line: "slow.next = null", indent: 1 },
    { line: "", indent: 0 },
    { line: "// Step 3: Reverse the second half", indent: 1 },
    { line: "prev = null", indent: 1 },
    { line: "while secondHalf:", indent: 1 },
    { line: "next = secondHalf.next", indent: 2 },
    { line: "secondHalf.next = prev", indent: 2 },
    { line: "prev = secondHalf", indent: 2 },
    { line: "secondHalf = next", indent: 2 },
    { line: "", indent: 0 },
    { line: "// Step 4: Merge the two halves", indent: 1 },
    { line: "first = head", indent: 1 },
    { line: "second = prev", indent: 1 },
    { line: "while second:", indent: 1 },
    { line: "temp1 = first.next", indent: 2 },
    { line: "temp2 = second.next", indent: 2 },
    { line: "first.next = second", indent: 2 },
    { line: "second.next = temp1", indent: 2 },
    { line: "first = temp1", indent: 2 },
    { line: "second = temp2", indent: 2 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (inputList) => {
    if (inputList.length <= 1) {
      return [
        {
          step: 0,
          list: inputList,
          phase: "complete",
          description: "List has 0 or 1 element, no reordering needed",
          highlightLine: 0,
          isFinal: true,
        },
      ];
    }

    const allSteps = [];
    let stepCount = 0;

    // Step 1: Find middle using slow/fast pointers
    allSteps.push({
      step: stepCount++,
      list: inputList,
      phase: "find-middle",
      description: "Step 1: Find middle using slow/fast pointers",
      highlightLine: 2,
      slowIdx: 0,
      fastIdx: 0,
    });

    let slowIdx = 0,
      fastIdx = 0;
    while (fastIdx < inputList.length - 1 && fastIdx < inputList.length - 2) {
      slowIdx++;
      fastIdx += 2;

      allSteps.push({
        step: stepCount++,
        list: inputList,
        phase: "find-middle",
        description: `Move slow to ${slowIdx}, fast to ${fastIdx}`,
        highlightLine: 4,
        slowIdx,
        fastIdx: Math.min(fastIdx, inputList.length - 1),
      });
    }

    const middleIdx = slowIdx;
    allSteps.push({
      step: stepCount++,
      list: inputList,
      phase: "find-middle",
      description: `Middle found at index ${middleIdx}`,
      highlightLine: 5,
      slowIdx: middleIdx,
      fastIdx: Math.min(fastIdx, inputList.length - 1),
      middleIdx,
    });

    // Step 2: Split the list
    const firstHalf = inputList.slice(0, middleIdx + 1);
    const secondHalf = inputList.slice(middleIdx + 1);

    allSteps.push({
      step: stepCount++,
      firstHalf,
      secondHalf,
      phase: "split",
      description: `Split into [${firstHalf.join(",")}] and [${secondHalf.join(
        ","
      )}]`,
      highlightLine: 8,
    });

    // Step 3: Reverse second half
    const reversedSecond = [...secondHalf];
    for (let i = 0; i < Math.floor(reversedSecond.length / 2); i++) {
      const temp = reversedSecond[i];
      reversedSecond[i] = reversedSecond[reversedSecond.length - 1 - i];
      reversedSecond[reversedSecond.length - 1 - i] = temp;

      allSteps.push({
        step: stepCount++,
        firstHalf,
        secondHalf: [...reversedSecond],
        phase: "reverse",
        description: `Reversing second half: swap positions ${i} and ${
          reversedSecond.length - 1 - i
        }`,
        highlightLine: 14,
        reverseIndices: [i, reversedSecond.length - 1 - i],
      });
    }

    allSteps.push({
      step: stepCount++,
      firstHalf,
      secondHalf: reversedSecond,
      phase: "reverse-complete",
      description: `Second half reversed: [${reversedSecond.join(",")}]`,
      highlightLine: 17,
    });

    // Step 4: Merge alternately
    const result = [];
    let i = 0,
      j = 0;
    let fromFirst = true;

    while (i < firstHalf.length || j < reversedSecond.length) {
      if (fromFirst && i < firstHalf.length) {
        result.push(firstHalf[i]);

        allSteps.push({
          step: stepCount++,
          firstHalf,
          secondHalf: reversedSecond,
          result: [...result],
          phase: "merge",
          description: `Add ${firstHalf[i]} from first half`,
          highlightLine: 25,
          mergeIdx1: i,
          mergeIdx2: j,
          addedFrom: "first",
        });

        i++;
      } else if (!fromFirst && j < reversedSecond.length) {
        result.push(reversedSecond[j]);

        allSteps.push({
          step: stepCount++,
          firstHalf,
          secondHalf: reversedSecond,
          result: [...result],
          phase: "merge",
          description: `Add ${reversedSecond[j]} from second half`,
          highlightLine: 25,
          mergeIdx1: i,
          mergeIdx2: j,
          addedFrom: "second",
        });

        j++;
      }

      fromFirst = !fromFirst;
    }

    // Final state
    allSteps.push({
      step: stepCount++,
      result,
      phase: "complete",
      description: `Reordering complete! Result: [${result.join(", ")}]`,
      highlightLine: 28,
      isFinal: true,
    });

    return allSteps;
  };

  // Initialize steps when test case changes
  useEffect(() => {
    const newSteps = calculateSteps(list);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
  }, [list]);

  // Auto-play functionality
  useEffect(() => {
    if (!isPlaying || finished) return;

    const timer = setTimeout(() => {
      handleNextStep();
    }, speed);

    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      const step = steps[nextStep];
      setHighlightedLine(step.highlightLine);
      setCurrentPhase(step.phase);

      if (step.isFinal) {
        setFinished(true);
        setIsPlaying(false);
      }
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      const step = steps[prevStep];
      setHighlightedLine(step.highlightLine);
      setCurrentPhase(step.phase);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
    setCurrentPhase("");
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setList(testCases[index].list);
  };

  const addCustomTestCase = () => {
    try {
      const cleanInput = customInput.trim().replace(/[\[\]]/g, "");
      const numbers = cleanInput.split(",").map((n) => parseInt(n.trim()));

      if (numbers.some(isNaN) || numbers.length === 0) {
        alert("Please enter valid numbers");
        return;
      }

      // Calculate expected result
      const expected = [];
      const mid = Math.floor(numbers.length / 2);
      const firstHalf = numbers.slice(0, mid);
      const secondHalf = numbers.slice(mid).reverse();

      let i = 0,
        j = 0;
      while (i < firstHalf.length || j < secondHalf.length) {
        if (i < firstHalf.length) expected.push(firstHalf[i++]);
        if (j < secondHalf.length) expected.push(secondHalf[j++]);
      }

      const newTestCase = {
        id: Date.now(),
        name: `Custom ${testCases.length - 5}`,
        list: numbers,
        expected: expected,
      };

      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
    } catch (error) {
      alert("Invalid input format");
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      const newTestCases = testCases.filter((tc) => tc.id !== id);
      setTestCases(newTestCases);
      if (selectedTestCase >= newTestCases.length) {
        handleTestCaseChange(0);
      }
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <Shuffle className="text-blue-500" size={24} />
        Reorder List
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left side - Visualization */}
        <div className="space-y-3">
          {/* Test Cases */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">
                      Input: [{tc.list.join(", ")}]
                    </div>
                    <div className="text-xs opacity-75">
                      Output: [{tc.expected.join(", ")}]
                    </div>
                  </button>
                  {tc.id > 6 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Add custom test case */}
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="Enter list: 1,2,3,4,5"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} />
                Add Test Case
              </button>
            </div>
          </div>

          {/* List Visualization */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            {/* Phase indicator */}
            <div className="mb-3 flex gap-2">
              {["find-middle", "split", "reverse", "merge"].map((phase) => (
                <div
                  key={phase}
                  className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                    currentStepData?.phase?.includes(phase)
                      ? "bg-blue-500 text-white"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {phase === "find-middle" && "1. Find Middle"}
                  {phase === "split" && "2. Split"}
                  {phase === "reverse" && "3. Reverse"}
                  {phase === "merge" && "4. Merge"}
                </div>
              ))}
            </div>

            {/* Original list with pointers (Phase 1) */}
            {currentStepData?.phase === "find-middle" && (
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-700 mb-2">
                  Finding Middle:
                </p>
                <div className="flex items-center gap-2">
                  {currentStepData.list.map((val, idx) => (
                    <div key={idx} className="relative">
                      <div
                        className={`w-12 h-12 flex items-center justify-center border-2 rounded transition-all duration-300 ${
                          idx === currentStepData.slowIdx
                            ? "bg-green-200 border-green-500 scale-110"
                            : idx === currentStepData.fastIdx
                            ? "bg-purple-200 border-purple-500 scale-110"
                            : idx === currentStepData.middleIdx
                            ? "bg-yellow-200 border-yellow-500"
                            : "bg-gray-100 border-gray-300"
                        }`}
                      >
                        <span className="font-mono font-bold text-sm">
                          {val}
                        </span>
                      </div>
                      <div className="text-xs text-center mt-1 text-gray-500">
                        [{idx}]
                      </div>

                      {/* Pointer labels */}
                      {idx === currentStepData.slowIdx && (
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs font-medium text-green-700">
                          slow
                        </div>
                      )}
                      {idx === currentStepData.fastIdx && (
                        <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs font-medium text-purple-700">
                          fast
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Split lists (Phases 2-4) */}
            {(currentStepData?.phase === "split" ||
              currentStepData?.phase === "reverse" ||
              currentStepData?.phase === "reverse-complete" ||
              currentStepData?.phase === "merge") && (
              <div className="space-y-3">
                {/* First Half */}
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-1">
                    First Half:
                  </p>
                  <div className="flex gap-1">
                    {currentStepData.firstHalf.map((val, idx) => (
                      <div
                        key={idx}
                        className={`w-10 h-10 flex items-center justify-center border-2 rounded transition-all ${
                          idx === currentStepData.mergeIdx1 &&
                          currentStepData.addedFrom === "first"
                            ? "bg-blue-200 border-blue-500 scale-110"
                            : "bg-blue-100 border-blue-300"
                        }`}
                      >
                        <span className="font-mono text-sm font-bold">
                          {val}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Second Half */}
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-1">
                    Second Half{" "}
                    {currentStepData.phase.includes("reverse")
                      ? "(Reversing)"
                      : "(Reversed)"}
                    :
                  </p>
                  <div className="flex gap-1">
                    {currentStepData.secondHalf.map((val, idx) => (
                      <div
                        key={idx}
                        className={`w-10 h-10 flex items-center justify-center border-2 rounded transition-all ${
                          currentStepData.reverseIndices?.includes(idx)
                            ? "bg-yellow-200 border-yellow-500 scale-110 animate-pulse"
                            : idx === currentStepData.mergeIdx2 &&
                              currentStepData.addedFrom === "second"
                            ? "bg-green-200 border-green-500 scale-110"
                            : "bg-green-100 border-green-300"
                        }`}
                      >
                        <span className="font-mono text-sm font-bold">
                          {val}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Merge Result */}
                {currentStepData.phase === "merge" && (
                  <div>
                    <p className="text-xs font-medium text-gray-700 mb-1">
                      Merged Result:
                    </p>
                    <div className="flex gap-1">
                      {currentStepData.result.map((val, idx) => (
                        <div
                          key={idx}
                          className={`w-10 h-10 flex items-center justify-center border-2 rounded transition-all ${
                            idx === currentStepData.result.length - 1
                              ? "bg-purple-200 border-purple-500 animate-pulse"
                              : "bg-purple-100 border-purple-300"
                          }`}
                        >
                          <span className="font-mono text-sm font-bold">
                            {val}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Final Result */}
            {currentStepData?.phase === "complete" && (
              <div className="p-3 bg-green-100 rounded">
                <h4 className="text-sm font-bold text-green-900 mb-2">
                  Reordering Complete!
                </h4>
                <div className="flex gap-1">
                  {currentStepData.result.map((val, idx) => (
                    <div
                      key={idx}
                      className="w-10 h-10 flex items-center justify-center bg-green-200 border-2 border-green-500 rounded"
                    >
                      <span className="font-mono text-sm font-bold">{val}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-green-700 mt-2">
                  Pattern: L0 → Ln → L1 → Ln-1 → L2 → Ln-2 → ...
                </p>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep === 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>

              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                {isPlaying ? "Pause" : "Play"}
              </button>

              <button
                onClick={handleNextStep}
                disabled={currentStep === steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>

              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} />
                Reset
              </button>

              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="2000"
                  step="200"
                  value={2200 - speed}
                  onChange={(e) => setSpeed(2200 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>

            {/* Step indicator */}
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right side - Pseudo Code & Analysis */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">Algorithm</h3>
            <div className="space-y-0.5 overflow-y-auto max-h-96">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>

          {/* Complexity Analysis */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Complexity Analysis</h3>
            <div className="space-y-2">
              <div>
                <h4 className="text-xs font-medium text-gray-700">
                  Time Complexity: O(n)
                </h4>
                <p className="text-xs text-gray-600">
                  Each step processes the list once: find middle O(n), reverse
                  O(n/2), merge O(n)
                </p>
              </div>
              <div>
                <h4 className="text-xs font-medium text-gray-700">
                  Space Complexity: O(1)
                </h4>
                <p className="text-xs text-gray-600">
                  Only using a constant amount of extra pointers
                </p>
              </div>
            </div>
          </div>

          {/* Algorithm Steps */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Algorithm Steps</h3>
            <div className="space-y-3">
              <div className="flex gap-2">
                <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                  1
                </div>
                <div className="flex-1">
                  <h4 className="text-xs font-medium">Find Middle</h4>
                  <p className="text-xs text-gray-600">
                    Use slow/fast pointers to find the middle node
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                  2
                </div>
                <div className="flex-1">
                  <h4 className="text-xs font-medium">Split List</h4>
                  <p className="text-xs text-gray-600">
                    Break the list into two halves at the middle
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                  3
                </div>
                <div className="flex-1">
                  <h4 className="text-xs font-medium">Reverse Second Half</h4>
                  <p className="text-xs text-gray-600">
                    Reverse the second half of the list
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                  4
                </div>
                <div className="flex-1">
                  <h4 className="text-xs font-medium">Merge Alternately</h4>
                  <p className="text-xs text-gray-600">
                    Take nodes alternately from both halves
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Key Insights */}
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The pattern is: first element, last element, second element,
                  second-last...
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Slow/fast pointer technique finds middle in one pass
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>Reversing the second half allows sequential merging</span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>In-place solution modifies pointers, not values</span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>Works for both even and odd length lists</span>
              </li>
            </ul>
          </div>

          {/* Visual Pattern */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Reordering Pattern</h3>
            <div className="space-y-2 text-xs">
              <div className="p-2 bg-gray-50 rounded">
                <p className="font-medium mb-1">Original:</p>
                <p className="font-mono">L0 → L1 → L2 → L3 → L4 → L5</p>
              </div>
              <div className="p-2 bg-gray-50 rounded">
                <p className="font-medium mb-1">After Split & Reverse:</p>
                <p className="font-mono">First: L0 → L1 → L2</p>
                <p className="font-mono">Second: L5 → L4 → L3</p>
              </div>
              <div className="p-2 bg-green-50 rounded">
                <p className="font-medium mb-1">Final Result:</p>
                <p className="font-mono">L0 → L5 → L1 → L4 → L2 → L3</p>
              </div>
            </div>
          </div>

          {/* Visual Guide */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Visual Guide</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-green-200 border-2 border-green-500 rounded"></div>
                <span>Slow pointer (finds middle)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-purple-200 border-2 border-purple-500 rounded"></div>
                <span>Fast pointer (2x speed)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-100 border-2 border-blue-300 rounded"></div>
                <span>First half nodes</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-green-100 border-2 border-green-300 rounded"></div>
                <span>Second half nodes (reversed)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-purple-100 border-2 border-purple-300 rounded"></div>
                <span>Merged result</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReorderListVisualizer;
